import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Table, Button, Alert } from 'reactstrap';
import {
  getBanners,
  createBanner,
  updateBanner,
  deleteBannerById,
  setCreateBanner,
  setUpdateBanner,
  setDeleteBannerById,
} from '../../redux/actions/banner';

import Loading from '../../components/Loading/Loading';
import ConfirmationModal from '../../components/Modal/ConfirmationModal';
import BannerModal from './subcomponents/BannerModal';
import { hasPermission } from '../../utils/permissions';

function BannerList() {
  const dispatch = useDispatch();
  const listState = useSelector((state) => state.banner.get);
  const createState = useSelector((state) => state.banner.create);
  const updateState = useSelector((state) => state.banner.update);
  const deleteState = useSelector((state) => state.banner.deleteById);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);

  const refresh = useCallback(() => {
    setLoading(true);
    dispatch(getBanners());
  }, [dispatch]);

  const handleCreate = useCallback(
    (formData) => {
      setLoading(true);
      dispatch(createBanner(formData));
    },
    [dispatch],
  );

  const handleEdit = useCallback(
    (id, formData) => {
      setLoading(true);
      dispatch(updateBanner(id, formData));
    },
    [dispatch],
  );

  const handleDelete = useCallback(
    (id) => {
      setLoading(true);
      dispatch(deleteBannerById(id));
    },
    [dispatch],
  );

  // Modal control methods
  const openCreateModal = () => {
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const openEditModal = (banner) => {
    setEditingBanner(banner);
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditingBanner(null);
    setIsEditModalOpen(false);
  };

  const onBannerCreate = (formData) => {
    handleCreate(formData);
    closeCreateModal();
  };

  const onBannerEdit = (formData) => {
    if (editingBanner) {
      handleEdit(editingBanner.id, formData);
      closeEditModal();
    }
  };

  const handleAlert = (type, message) => {
    setAlertType(type);
    setAlertMessage(message);
    setShowAlert(true);
    const timer = setTimeout(() => setShowAlert(false), 3000);
    return () => clearTimeout(timer);
  };

  const clearReduxState = useCallback(
    (actionCreator, delay = 3500) => {
      setTimeout(() => {
        dispatch(actionCreator(null));
      }, delay);
    },
    [dispatch],
  );

  useEffect(() => {
    document.title = 'BGL - Banners';
    // Solo carga inicial
    setLoading(true);
    dispatch(getBanners());
  }, [dispatch]);

  // Efecto separado para manejar respuestas de acciones
  useEffect(() => {
    if (listState) {
      setData(listState.data || []);
      setLoading(false);
    }
  }, [listState]);

  useEffect(() => {
    if (createState) {
      handleAlert(createState.status, createState.message);
      setLoading(false);
      // Solo refresca si fue exitoso
      if (createState.status === 'success') {
        dispatch(getBanners());
      }
      clearReduxState(setCreateBanner);
    }
  }, [createState, clearReduxState, dispatch]);

  useEffect(() => {
    if (updateState) {
      handleAlert(updateState.status, updateState.message);
      setLoading(false);
      // Solo refresca si fue exitoso
      if (updateState.status === 'success') {
        dispatch(getBanners());
      }
      clearReduxState(setUpdateBanner);
    }
  }, [updateState, clearReduxState, dispatch]);

  useEffect(() => {
    if (deleteState) {
      handleAlert(deleteState.status, deleteState.message);
      setLoading(false);
      // Solo refresca si fue exitoso
      if (deleteState.status === 'success') {
        dispatch(getBanners());
      }
      clearReduxState(setDeleteBannerById);
    }
  }, [deleteState, clearReduxState, dispatch]);

  return loading ? (
    <Loading />
  ) : (
    <>
      <Container>
        <Alert
          color={alertType}
          isOpen={showAlert}
          toggle={() => setShowAlert(false)}
        >
          {alertMessage}
        </Alert>
      </Container>

      <Container className="container-border p-3">
        <article>
          {hasPermission('banners.create') && (
            <Button
              className="float-right mb-2"
              size="sm"
              onClick={openCreateModal}
            >
              <i className="fa fa-plus mr-3"></i>
              <span>Crear banner</span>
            </Button>
          )}
          <Button
            className="float-right mb-2"
            size="sm"
            onClick={() => refresh()}
          >
            <i className="fa fa-sync mr-3"></i>
            <span>Refrescar</span>
          </Button>
          <h2>Lista de Banners</h2>
        </article>

        <Table hover>
          <thead>
            <tr>
              <th>ID</th>
              <th>Imagen</th>
              <th>Título</th>
              <th>Subtítulo</th>
              <th>Call Action</th>
              <th>Activo</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {data.length === 0 && (
              <tr>
                <td colSpan="7" className="text-center">
                  No hay banners disponibles
                </td>
              </tr>
            )}

            {data.map((row) => {
              return (
                <tr key={row.id}>
                  <td>{row.id}</td>
                  <td>
                    {row.imagen_url ? (
                      <img
                        src={row.imagen_url}
                        alt={row.titulo}
                        style={{ maxWidth: '100px', maxHeight: '50px' }}
                      />
                    ) : (
                      'Sin imagen'
                    )}
                  </td>
                  <td>{row.titulo}</td>
                  <td>{row.subtitulo}</td>
                  <td>{row.call_action ? 'Sí' : 'No'}</td>
                  <td>{row.active ? 'Sí' : 'No'}</td>
                  <td>
                    <>
                      {hasPermission('banners.edit') && (
                        <Button
                          className="mr-2"
                          onClick={() => openEditModal(row)}
                        >
                          <i className="fa fa-edit"></i>
                        </Button>
                      )}
                      {hasPermission('banners.delete') && (
                        <ConfirmationModal
                          btnClass="btn btn-danger"
                          iconClass="fa fa-trash"
                          title="Eliminar banner"
                          message="¿Estas seguro que quieres eliminar este banner? Esta operación no se podrá deshacer."
                          action={() => handleDelete(row.id)}
                          argument={row.id}
                          successMessage="Eliminar"
                          cancelMessage="Cancelar"
                          btnSuccessState="danger"
                          btnCancelState="secondary"
                        />
                      )}
                    </>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Container>

      {/* Create Banner Modal */}
      <BannerModal
        isOpen={isCreateModalOpen}
        onToggle={closeCreateModal}
        title="Crear banner"
        onSubmit={onBannerCreate}
      />

      {/* Edit Banner Modal */}
      <BannerModal
        isOpen={isEditModalOpen}
        onToggle={closeEditModal}
        title="Editar banner"
        defaultValue={editingBanner}
        onSubmit={onBannerEdit}
      />
    </>
  );
}

export default BannerList;

